import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import axios from "axios";
import config from "../config";

const PasswordResetPage = ({ accountType = "patient", loginPath = "/elder-login" }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState(searchParams.get("email") || "");
  const [code, setCode] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPasswords, setShowPasswords] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (!resendCooldown) return undefined;
    const timer = window.setInterval(() => {
      setResendCooldown((seconds) => Math.max(0, seconds - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [resendCooldown]);

  const requestCode = async () => {
    const response = await axios.post(`${config.API_BASE_URL}/auth/forgot-password`, {
      email,
      accountType
    });
    setStep("code");
    setResendCooldown(60);
    setMessage(response.data.message);
  };

  const resendCode = async () => {
    if (resendCooldown || loading) return;
    setLoading(true);
    setError("");
    setMessage("");
    try {
      await requestCode();
    } catch (requestError) {
      setError(requestError.response?.data?.error || "Unable to resend the verification code.");
    } finally {
      setLoading(false);
    }
  };

  const submit = async (event) => {
    event.preventDefault();
    setError("");
    setMessage("");
    if (step === "password" && password !== confirmPassword) {
      setError("Password and confirmation must match.");
      return;
    }

    setLoading(true);
    try {
      if (step === "email") {
        await requestCode();
      } else if (step === "code") {
        const response = await axios.post(`${config.API_BASE_URL}/auth/verify-reset-code`, {
          email,
          code,
          accountType
        });
        setResetToken(response.data.resetToken);
        setStep("password");
        setMessage(response.data.message);
      } else if (step === "password") {
        const response = await axios.post(`${config.API_BASE_URL}/auth/reset-password`, {
          resetToken,
          password,
          accountType
        });
        setPassword("");
        setConfirmPassword("");
        setResetToken("");
        setStep("done");
        setMessage(response.data.message);
      }
    } catch (requestError) {
      setError(requestError.response?.data?.error || "Unable to complete password recovery.");
    } finally {
      setLoading(false);
    }
  };

  const titles = {
    email: "Forgot your password?",
    code: "Enter your verification code",
    password: "Choose a new password",
    done: "Password reset complete"
  };
  const descriptions = {
    email: "Enter your account email and we’ll send a 6-digit code that expires in 10 minutes.",
    code: `Enter the code sent to ${email}.`,
    password: "Create a new password with at least 8 characters.",
    done: "You can now sign in with your new password."
  };

  return (
    <div className="page-shell">
      <section className="page-panel">
        <div className="page-content auth-layout">
          <div className="support-card">
            <div className="intro-badge">Account recovery</div>
            <h1 className="page-title">{titles[step]}</h1>
            <p className="page-subtitle">{descriptions[step]}</p>
          </div>
          <form className="form-card" onSubmit={submit}>
            {message && <div className="success-banner">{message}</div>}
            {error && <div className="error-banner">{error}</div>}

            {step === "email" && (
              <div className="form-field full">
                <label htmlFor="reset-email">Email address</label>
                <input id="reset-email" className="text-input" type="email" autoComplete="email" required value={email} onChange={(event) => setEmail(event.target.value)} />
              </div>
            )}

            {step === "code" && (
              <div className="form-field full">
                <label htmlFor="reset-code">6-digit code</label>
                <input id="reset-code" className="text-input" type="text" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6} required value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, ""))} />
                <button className="simple-link" type="button" disabled={Boolean(resendCooldown) || loading} onClick={resendCode}>
                  {resendCooldown ? `Resend code in ${resendCooldown}s` : "Resend code"}
                </button>
              </div>
            )}

            {step === "password" && (
              <>
                <div className="form-field full">
                  <label htmlFor="reset-password">New password</label>
                  <div style={{ position: "relative" }}>
                    <input id="reset-password" className="text-input" style={{ paddingRight: "3.5rem" }} type={showPasswords ? "text" : "password"} autoComplete="new-password" minLength={8} required value={password} onChange={(event) => setPassword(event.target.value)} />
                    <button type="button" style={passwordEyeButton} aria-label={showPasswords ? "Hide passwords" : "Show passwords"} onClick={() => setShowPasswords((shown) => !shown)}>{showPasswords ? <FaEyeSlash /> : <FaEye />}</button>
                  </div>
                </div>
                <div className="form-field full">
                  <label htmlFor="reset-confirm-password">Confirm new password</label>
                  <input id="reset-confirm-password" className="text-input" type={showPasswords ? "text" : "password"} autoComplete="new-password" minLength={8} required value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} />
                </div>
              </>
            )}

            <div className="button-row">
              {step === "code" ? (
                <button className="ghost-button" type="button" onClick={() => { setStep("email"); setCode(""); setMessage(""); setError(""); }}>Change email</button>
              ) : (
                <button className="ghost-button" type="button" onClick={() => navigate(loginPath)}>Back to sign in</button>
              )}
              {step !== "done" && <button className="warm-button" type="submit" disabled={loading}>{loading ? "Please wait…" : step === "email" ? "Send code" : step === "code" ? "Verify code" : "Reset password"}</button>}
            </div>
          </form>
        </div>
      </section>
    </div>
  );
};

const passwordEyeButton = { position: "absolute", right: "1rem", top: "50%", transform: "translateY(-50%)", border: "none", background: "transparent", color: "#5f6c76", padding: 0 };

export default PasswordResetPage;
